import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Input, message } from 'antd';
import { Copy, ShieldCheck } from '@phosphor-icons/react';
import { captureAnalytics } from '../../../../actions/preload';
import './authenticatorAppSetting.scss';

// Authenticator-app sign-in codes for the User Information table, next to the
// cell-phone editor. Like that editor it is an inline step machine:
//   idle -> setup_password -> setup_scan -> idle   (set up / set up on a new device)
//   idle -> remove_password -> idle                (remove)
// The pending TOTP secret lives only in this component's state while the scan
// step is showing; every exit from that step drops it.

export const STEPS = {
  idle: 'idle',
  setupPassword: 'setup_password',
  setupScan: 'setup_scan',
  removePassword: 'remove_password',
};

export const COPY = {
  label: 'Authenticator App',
  off: 'Not set up',
  on: 'On',
  pitch: 'Get sign-in codes from an app like 1Password, Authy, or Google Authenticator instead of waiting for an email or text.',
  replaceWarning: 'Setting up a new device replaces the one you use now. Codes from the old device stop working once you finish.',
  removePrompt: 'Remove your authenticator app? You\'ll get sign-in codes by email or text instead.',
  removed: 'You\'ll get sign-in codes by email or text again.',
  impersonating: 'Authenticator app settings can\'t be changed while impersonating a user.',
  passwordLabel: 'Confirm your password',
  passwordRequired: 'Enter your password to continue.',
  scanTitle: 'Scan this QR code with your authenticator app',
  manualKey: 'Can\'t scan it? Enter this key instead:',
  codeLabel: 'Enter the 6-digit code from the app',
  setupExpired: 'Setup expired. Please start again.',
  copyFailed: 'Couldn\'t copy the key. Select it and copy it instead.',
  requestFailed: 'Something went wrong. Please try again.',
};

const EMPTY_FLOW = {
  step: STEPS.idle,
  replacing: false,
  password: '',
  code: '',
  setup: null,
  busy: false,
  error: '',
  copied: false,
};

// The confirm endpoint answers 410 when the pending secret is gone (expired or
// too many wrong codes), so setup has to start again; 422 means try another code.
const SETUP_GONE_STATUS = 410;

// Only render an inline image; anything else is ignored rather than fetched.
const isImageDataUrl = (value) => typeof value === 'string' && /^data:image\/(png|svg\+xml|gif|jpeg)[;,]/i.test(value);

export const groupSecret = (secret) => (
  (String(secret || '').replace(/\s/g, '').match(/.{1,4}/g) || []).join(' ')
);

const formatAddedDate = (enabledAt) => {
  const date = moment(enabledAt);
  return date.isValid() ? date.format('MMM D, YYYY') : '';
};

const runRequest = async (request, value) => {
  try {
    return (await request(value)) || { error: COPY.requestFailed };
  } catch {
    return { error: COPY.requestFailed };
  }
};

const AuthenticatorAppSetting = ({
  enabledAt,
  impersonating,
  onSetup,
  onConfirm,
  onRemove,
  captureAnalyticsEvent,
}) => {
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [notice, setNotice] = useState('');
  // Bumped whenever the user leaves a step, so a response that lands after
  // Cancel (or after unmount) is ignored instead of reviving the old step.
  const requestId = useRef(0);

  const update = (patch) => setFlow((current) => ({ ...current, ...patch }));
  const enabled = Boolean(enabledAt);
  const { step, setup } = flow;

  useEffect(() => () => { requestId.current += 1; }, []);

  // The server forgets the pending secret after `expires_in` seconds. Take the
  // QR code and key off the screen at the same moment, unless a Verify is in
  // flight: its answer, not the timer, then decides what the user sees.
  useEffect(() => {
    if (step !== STEPS.setupScan || !setup?.expiresAt || flow.busy) return undefined;
    const timer = setTimeout(() => {
      requestId.current += 1;
      setFlow((current) => ({
        ...EMPTY_FLOW,
        step: STEPS.setupPassword,
        replacing: current.replacing,
        error: COPY.setupExpired,
      }));
    }, Math.max(0, setup.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [step, setup, flow.busy]);

  const beginRequest = () => {
    requestId.current += 1;
    update({ busy: true, error: '' });
    return requestId.current;
  };
  const isStale = (token) => token !== requestId.current;

  const startSetup = () => {
    setNotice('');
    requestId.current += 1;
    setFlow({ ...EMPTY_FLOW, step: STEPS.setupPassword, replacing: enabled });
  };

  const startRemove = () => {
    setNotice('');
    requestId.current += 1;
    setFlow({ ...EMPTY_FLOW, step: STEPS.removePassword });
  };

  // Confirm and remove commit on the server before they answer, so Cancel
  // cannot undo one in flight; it would only hide the outcome. A setup
  // request writes nothing lasting, so it stays cancellable.
  const cancelLocked = flow.busy && step !== STEPS.setupPassword;

  const cancel = () => {
    if (cancelLocked) return;
    requestId.current += 1;
    setFlow(EMPTY_FLOW);
  };

  const submitSetupPassword = async () => {
    if (flow.busy) return;
    if (!flow.password) {
      update({ error: COPY.passwordRequired });
      return;
    }
    const token = beginRequest();
    const result = await runRequest(onSetup, flow.password);
    if (isStale(token)) return;
    if (result.error) {
      update({ busy: false, password: '', error: result.error });
      return;
    }
    const { secret, qr_code: qrCode, expires_in: expiresIn } = result.data || {};
    if (!secret) {
      update({ busy: false, password: '', error: COPY.requestFailed });
      return;
    }
    const ttlSeconds = Number(expiresIn) || 0;
    setFlow((current) => ({
      ...EMPTY_FLOW,
      step: STEPS.setupScan,
      replacing: current.replacing,
      setup: { secret, qrCode, expiresAt: ttlSeconds > 0 ? Date.now() + (ttlSeconds * 1000) : null },
    }));
  };

  const submitCode = async () => {
    if (flow.busy || flow.code.length !== 6) return;
    const { replacing } = flow;
    const token = beginRequest();
    const result = await runRequest(onConfirm, flow.code);
    if (isStale(token)) return;
    if (result.error) {
      if (result.status === SETUP_GONE_STATUS) {
        // The pending secret is gone server-side; drop ours too.
        setFlow({
          ...EMPTY_FLOW, step: STEPS.setupPassword, replacing, error: result.error,
        });
        return;
      }
      update({ busy: false, code: '', error: result.error });
      return;
    }
    requestId.current += 1;
    setFlow(EMPTY_FLOW);
    captureAnalyticsEvent('authenticator_app_setup_completed', { replacing });
    message.success('Authenticator app added');
  };

  const submitRemove = async () => {
    if (flow.busy) return;
    if (!flow.password) {
      update({ error: COPY.passwordRequired });
      return;
    }
    const token = beginRequest();
    const result = await runRequest(onRemove, flow.password);
    if (isStale(token)) return;
    if (result.error) {
      update({ busy: false, password: '', error: result.error });
      return;
    }
    requestId.current += 1;
    setFlow(EMPTY_FLOW);
    setNotice(COPY.removed);
    captureAnalyticsEvent('authenticator_app_removed');
    message.success('Authenticator app removed');
  };

  const copySecret = async () => {
    const token = requestId.current;
    try {
      await navigator.clipboard.writeText(setup.secret);
      if (!isStale(token)) update({ copied: true });
    } catch {
      message.error(COPY.copyFailed);
    }
  };

  const renderError = () => (
    flow.error ? <div className="authenticator-app__error" role="alert">{flow.error}</div> : null
  );

  const renderPasswordStep = ({
    intro, submitLabel, onSubmit, danger,
  }) => (
    <div className="authenticator-app__step">
      {intro && <div className="authenticator-app__hint">{intro}</div>}
      <label className="authenticator-app__label" htmlFor="authenticator-app-password">
        {COPY.passwordLabel}
      </label>
      <div className="authenticator-app__row">
        <Input
          id="authenticator-app-password"
          className="authenticator-app__password"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={flow.password}
          // readOnly rather than disabled keeps focus, so a rejected entry can be retyped at once.
          readOnly={flow.busy}
          onChange={(event) => update({ password: event.target.value, error: '' })}
          onPressEnter={onSubmit}
        />
        <Button
          type="primary"
          danger={danger}
          className={danger ? 'authenticator-app__danger' : undefined}
          loading={flow.busy}
          disabled={!flow.password}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
        <Button type="link" className="authenticator-app__action" disabled={cancelLocked} onClick={cancel}>
          Cancel
        </Button>
      </div>
      {renderError()}
    </div>
  );

  const renderScanStep = () => (
    <div className="authenticator-app__step">
      <div className="authenticator-app__title">{COPY.scanTitle}</div>
      {/* ph-no-capture keeps the QR code and key (the sign-in secret) out of
          PostHog session replays; replay masks only input values by default. */}
      <div className="authenticator-app__scan ph-no-capture">
        {isImageDataUrl(setup.qrCode) && (
          <img
            className="authenticator-app__qr"
            src={setup.qrCode}
            alt="QR code to add Medsender to your authenticator app"
            width={168}
            height={168}
          />
        )}
        <div className="authenticator-app__manual">
          <div className="authenticator-app__hint">{COPY.manualKey}</div>
          <div className="authenticator-app__key-row">
            <code className="authenticator-app__key">{groupSecret(setup.secret)}</code>
            <Button
              type="link"
              className="authenticator-app__action authenticator-app__copy"
              icon={<Copy size={16} weight="bold" />}
              onClick={copySecret}
            >
              {flow.copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </div>
      <label className="authenticator-app__label" htmlFor="authenticator-app-code">
        {COPY.codeLabel}
      </label>
      <div className="authenticator-app__row">
        <Input
          id="authenticator-app-code"
          className="authenticator-app__code"
          inputMode="numeric"
          autoComplete="one-time-code"
          // No maxLength: the browser would cut a pasted "123 456" to "123 45"
          // before onChange strips the separator.
          placeholder="6-digit code"
          value={flow.code}
          readOnly={flow.busy}
          onChange={(event) => update({ code: event.target.value.replace(/\D/g, '').slice(0, 6), error: '' })}
          onPressEnter={submitCode}
        />
        <Button
          type="primary"
          loading={flow.busy}
          disabled={flow.code.length !== 6}
          onClick={submitCode}
        >
          Verify
        </Button>
        <Button type="link" className="authenticator-app__action" disabled={cancelLocked} onClick={cancel}>
          Cancel
        </Button>
      </div>
      {renderError()}
    </div>
  );

  const renderIdle = () => (
    <>
      <div className="authenticator-app__status">
        {enabled ? (
          <span className="authenticator-app__badge authenticator-app__badge--on">
            <ShieldCheck size={16} weight="fill" />
            {COPY.on}
          </span>
        ) : (
          <span className="authenticator-app__badge">{COPY.off}</span>
        )}
        {enabled && formatAddedDate(enabledAt) && (
          <span className="authenticator-app__meta">{`Added ${formatAddedDate(enabledAt)}`}</span>
        )}
        {!impersonating && (enabled ? (
          <>
            <Button type="link" className="authenticator-app__action" onClick={startSetup}>
              Set up on a new device
            </Button>
            <Button type="link" danger className="authenticator-app__action authenticator-app__action--danger" onClick={startRemove}>
              Remove
            </Button>
          </>
        ) : (
          <Button type="primary" className="authenticator-app__setup" onClick={startSetup}>Set up</Button>
        ))}
      </div>
      {!enabled && <div className="authenticator-app__hint">{COPY.pitch}</div>}
      {notice && <div className="authenticator-app__notice" role="status">{notice}</div>}
      {impersonating && <div className="authenticator-app__hint">{COPY.impersonating}</div>}
    </>
  );

  return (
    <div className="authenticator-app">
      {step === STEPS.idle && renderIdle()}
      {step === STEPS.setupPassword && renderPasswordStep({
        intro: flow.replacing ? COPY.replaceWarning : null,
        submitLabel: 'Continue',
        onSubmit: submitSetupPassword,
      })}
      {step === STEPS.setupScan && setup && renderScanStep()}
      {step === STEPS.removePassword && renderPasswordStep({
        intro: COPY.removePrompt,
        submitLabel: 'Remove',
        onSubmit: submitRemove,
        danger: true,
      })}
    </div>
  );
};

AuthenticatorAppSetting.propTypes = {
  enabledAt: PropTypes.string,
  impersonating: PropTypes.bool,
  onSetup: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  captureAnalyticsEvent: PropTypes.func,
};

AuthenticatorAppSetting.defaultProps = {
  enabledAt: null,
  impersonating: false,
  captureAnalyticsEvent: captureAnalytics,
};

export default AuthenticatorAppSetting;
